import { FC, HTMLAttributes, useEffect, useRef } from "react";
import clsx from "classnames";
import { useStyles } from "./Dialog.style";
import Button, { IButtonProps } from "../Button/Button";
import { Typography } from "../Typography/Typography";
import { TypographyVariant } from "../../../constants/typography";
import { useTranslation } from "react-i18next";

interface IButtonAction {
  text: string | React.ReactNode;
  icon?: React.HTMLAttributes<HTMLDivElement>;
  loading?: boolean;
  isCloseButton?: boolean;
}

interface IDialogProps {
  open: boolean;
  dialogRef?: React.MutableRefObject<HTMLDialogElement | null>;
  mainAction?: IButtonAction & IButtonProps & HTMLAttributes<HTMLDivElement>;
  secondAction?: IButtonAction & IButtonProps & HTMLAttributes<HTMLDivElement>;
  onClose?: () => void;
  stickBottom?: boolean;
  enableScrollBackground?: boolean;
  hideLayout?: boolean;
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  loadingText?: string;
  rootClassName?: string;
  footer?: React.ReactNode;
  footerClassName?: string;
}

export const Dialog: FC<IDialogProps & HTMLAttributes<HTMLDialogElement>> = ({
  open = false,
  dialogRef,
  mainAction,
  secondAction,
  children,
  hideLayout,
  title,
  description,
  icon,
  loadingText,
  rootClassName,
  footerClassName,
  onClose,
  footer,
  ...otherProps
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const ref = useRef<HTMLDialogElement>(null);
  const scrollPositionRef = useRef<number>(0);

  const setDialogRef = (element: HTMLDialogElement | null) => {
    ref.current = element;
    if (dialogRef) dialogRef.current = element;
  };

  const closeModal = () => {
    ref.current?.close?.();
  };

  const showModal = () => {
    ref.current?.showModal?.();
  };

  const lockScroll = () => {
    scrollPositionRef.current = window.scrollY;
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.width = "100vw";
    document.body.style.height = "100vh";
    document.body.style.top = `-${scrollPositionRef.current}px`;
  };

  const unlockScroll = () => {
    document.body.style.removeProperty("overflow");
    document.body.style.removeProperty("position");
    document.body.style.removeProperty("width");
    document.body.style.removeProperty("height");
    window.scrollTo(0, scrollPositionRef.current);
  };

  // Handle cancel event
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog || !onClose) return;

    const handleCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };
    dialog.addEventListener("cancel", handleCancel);
    return () => dialog.removeEventListener("cancel", handleCancel);
  }, [onClose, open]);

  useEffect(() => {
    if (!ref.current) return;
    if (open) {
      lockScroll();
      showModal();
    } else {
      unlockScroll();
      closeModal();
    }
    return () => {
      unlockScroll();
    };
  }, [open, ref]);

  if (!open) {
    return null;
  }

  /**
   * Hidden focusable element
   * prevents native scrollIntoView side-effect on TV WebKit (LG WebOS / Tizen)
   */
  const focusAbsorber = (
    <span
      tabIndex={0}
      autoFocus
      style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
    />
  );

  if (hideLayout)
    return (
      <dialog {...otherProps} ref={setDialogRef}>
        {focusAbsorber}
        {children}
      </dialog>
    );

  return (
    <dialog
      {...otherProps}
      ref={setDialogRef}
      className={clsx(classes.root, { open }, rootClassName)}
    >
      {focusAbsorber}
      <div className={classes.rootContent}>
        {icon && <div className={classes.icon}>{icon}</div>}
        {children ? (
          <div className={clsx(classes.content, otherProps.className)}>
            {children}
          </div>
        ) : (
          title && (
            <div className={classes.content}>
              <Typography
                variant={TypographyVariant.HeadingH5Bold}
                className={classes.title}
              >
                {title}
              </Typography>
              <Typography
                variant={TypographyVariant.BodyB6Medium}
                className={classes.description}
              >
                {description}
              </Typography>
            </div>
          )
        )}
      </div>
      <div className={classes.actions}>
        {loadingText && (
          <Button
            variant="contained"
            fullWidth
            loadingText={loadingText}
            disabled
            className={clsx(mainAction?.className, classes.btn)}
          />
        )}
        {mainAction && !loadingText && (
          <Button
            variant="contained"
            fullWidth
            loading={mainAction?.loading}
            {...mainAction}
            className={clsx(mainAction?.className, classes.btn)}
          >
            {mainAction?.text || t("Dialog_Ok")}
          </Button>
        )}
        {secondAction && !loadingText && (
          <Button
            variant="outlined"
            color="secondary"
            fullWidth
            loading={secondAction?.loading}
            {...secondAction}
            className={clsx(secondAction?.className, classes.btn)}
          >
            {secondAction.text}
          </Button>
        )}
      </div>
      {footer && (
        <div className={clsx(classes.footer, footerClassName)}>{footer}</div>
      )}
    </dialog>
  );
};
